/**
 * Parseo de planilla + correspondencia de columnas + resolucion de unidad --
 * portado del proyecto original (rama `main`, commit 5979987,
 * `apps/web/src/lib/importApi.ts`), adaptado al backend nuevo: alli
 * `resolveUnit` cruzaba contra `name_short`/`name_official`/`code` de una
 * tabla de unidades mas rica (con alias); aqui `organizational_units` solo
 * tiene `name`, asi que el match usa unicamente ese campo (exacto o parcial,
 * sin acentos) -- mismo principio, superficie mas chica.
 *
 * "Rescatar tablas de cualquier Excel": SheetJS (`xlsx`) lee TODAS las hojas
 * del archivo y expone sus headers/filas -- el usuario elige cual hoja
 * importar, no hace falta que el archivo tenga una forma fija.
 */
import type { ImportKind } from './queries';

export interface UnitOption {
  id: string;
  name: string;
}

export interface ParsedSheet {
  sheet: string;
  headers: string[];
  rows: Record<string, unknown>[];
}

/** Parseo perezoso: xlsx (SheetJS, ~140 KB gzip) solo se carga al entrar en la importación. */
export async function parseSpreadsheet(file: File): Promise<ParsedSheet[]> {
  const XLSX = await import('xlsx');
  const isCsv = /\.csv$/i.test(file.name) || file.type === 'text/csv';
  // CSV: leer como texto UTF-8 (evita que los acentos se rompan por codepage).
  const src = isCsv ? await file.text() : await file.arrayBuffer();
  const wb = XLSX.read(src, isCsv ? { type: 'string' } : { type: 'array' });
  return wb.SheetNames.map((name) => {
    const ws = wb.Sheets[name]!;
    const json = XLSX.utils.sheet_to_json<Record<string, unknown>>(ws, { defval: '' });
    const headers = json.length ? Object.keys(json[0]!) : [];
    return { sheet: name, headers, rows: json };
  }).filter((s) => s.rows.length > 0);
}

const norm = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

/** Empareja el valor de una celda "unidad" con una unidad canónica por nombre (exacto o parcial, sin acentos). */
export function resolveUnit(value: unknown, units: UnitOption[]): string | null {
  const v = norm(String(value ?? ''));
  if (!v) return null;
  const exact = units.find((u) => norm(u.name) === v);
  if (exact) return exact.id;
  const partial = units.find((u) => norm(u.name).includes(v) || v.includes(norm(u.name)));
  return partial?.id ?? null;
}

export interface ImportField {
  key: string;
  label: string;
  required?: boolean;
}

export const IMPORT_FIELDS: Record<ImportKind, ImportField[]> = {
  contact: [
    { key: 'full_name', label: 'Nombre', required: true },
    { key: 'position', label: 'Cargo' },
    { key: 'email', label: 'Correo' },
    { key: 'phone', label: 'Teléfono' },
    { key: 'notes', label: 'Notas' },
    { key: 'unidad', label: 'Unidad (texto)', required: true },
  ],
  activity_seed: [
    { key: 'title', label: 'Nombre de la actividad', required: true },
    { key: 'description', label: 'Descripción' },
    { key: 'notes', label: 'Notas' },
    { key: 'unidad', label: 'Unidad (texto)', required: true },
  ],
};

/** Adivina qué campo destino corresponde a cada columna de origen, por palabras clave en el encabezado. */
export function guessMapping(headers: string[], kind: ImportKind): Record<string, string> {
  const targets: Record<string, string[]> =
    kind === 'contact'
      ? {
          full_name: ['nombre', 'contacto', 'responsable', 'persona'],
          position: ['cargo', 'rol', 'puesto'],
          email: ['correo', 'email', 'mail'],
          phone: ['telefono', 'fono', 'celular', 'anexo'],
          unidad: ['unidad', 'direccion', 'area', 'departamento'],
          notes: ['nota', 'observ', 'comentario'],
        }
      : {
          // 'description' se comprueba ANTES que 'title': con las palabras
          // clave del original, un encabezado como "Descripción del Proceso"
          // matcheaba "proceso" (de title) antes de llegar a "descripcion"
          // -- las palabras clave de description son mas especificas
          // (dificil que aparezcan en una columna que de verdad es el
          // titulo), asi que van primero.
          description: ['descripcion', 'detalle', 'finalidad'],
          title: ['actividad', 'nombre', 'titulo', 'proceso', 'tratamiento'],
          unidad: ['unidad', 'direccion', 'area', 'departamento'],
          notes: ['nota', 'observ', 'comentario'],
        };
  const out: Record<string, string> = {};
  for (const h of headers) {
    const n = norm(h);
    for (const [field, keys] of Object.entries(targets)) {
      if (keys.some((k) => n.includes(k))) {
        out[h] = field;
        break;
      }
    }
  }
  return out;
}

export interface StagedRow {
  source_row_no: number;
  mapped: Record<string, string>;
  resolved_unit_id: string | null;
  include: boolean;
}

/** Reconstruye las filas en curación a partir del parseo crudo + la correspondencia de columnas vigente. */
export function buildStagedRows(sheet: ParsedSheet, mapping: Record<string, string>, units: UnitOption[]): StagedRow[] {
  const unitCol = Object.entries(mapping).find(([, f]) => f === 'unidad')?.[0];
  return sheet.rows.map((raw, i) => {
    const mapped: Record<string, string> = {};
    for (const [col, field] of Object.entries(mapping)) {
      if (field && field !== 'unidad') mapped[field] = String(raw[col] ?? '').trim();
    }
    const unitVal = unitCol ? raw[unitCol] : '';
    return {
      source_row_no: i + 2, // +2: la fila 1 es el encabezado
      mapped,
      resolved_unit_id: resolveUnit(unitVal, units),
      include: true,
    };
  });
}
